import React from 'react';
import { Autocomplete, Box, TextField } from '@mui/material';

import { Column, ColumnName } from '@models/column';
import { isSet, Unset } from '@utils';

import {
  SourceTypeInteger,
  SourceTypeBoolean,
  SourceTypeDate,
  SourceTypeDouble,
  SourceTypeFloat,
  SourceTypeLong,
  SourceTypeShort,
  SourceTypeString,
  SourceTypeStruct,
  SourceTypeTimestamp,
  SourceTypeUnknown,
  SourceTypeVectorUDT,
  SourceTypeBigInteger,
} from '../../../../components/column-source-type-icons';

import { InlineEditor, InlineEditorProps, Widget } from './_InlineDraftEditor';

const sourceTypeIcon: Record<Column.SourceType, JSX.Element> = {
  [Column.SourceType.Boolean]: <SourceTypeBoolean size={20} />,
  [Column.SourceType.Date]: <SourceTypeDate size={20} />,
  [Column.SourceType.Double]: <SourceTypeDouble size={20} />,
  [Column.SourceType.Float]: <SourceTypeFloat size={20} />,
  [Column.SourceType.Integer]: <SourceTypeInteger size={20} />,
  [Column.SourceType.BigInteger]: <SourceTypeBigInteger size={20} />,
  [Column.SourceType.Long]: <SourceTypeLong size={20} />,
  [Column.SourceType.Short]: <SourceTypeShort size={20} />,
  [Column.SourceType.String]: <SourceTypeString size={20} />,
  [Column.SourceType.Struct]: <SourceTypeStruct size={20} />,
  [Column.SourceType.Timestamp]: <SourceTypeTimestamp size={20} />,
  [Column.SourceType.VectorUDT]: <SourceTypeVectorUDT size={20} />,
};

export const ColumnWidget = (
  props: {
    columns: Column[];
  } & Widget<ColumnName | Unset, Column>
) => {
  const selectedColumn = isSet(props.value)
    ? props.columns.find(({ name }) => name === props.value)
    : undefined;

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', top: '2px' }}>
        {selectedColumn
          ? sourceTypeIcon[selectedColumn.sourceType] ?? (
              <SourceTypeUnknown size={20} />
            )
          : null}
      </div>
      <Autocomplete
        style={{ width: '180px' }}
        sx={{
          '& .MuiInput-root.MuiInputBase-sizeSmall .MuiInput-input': {
            paddingLeft: selectedColumn ? '26px' : 'revert',
          },
        }}
        options={props.columns}
        getOptionLabel={(column: Column) => column.name}
        renderOption={(props, column) => (
          <Box
            component="li"
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
            {...props}
          >
            {sourceTypeIcon[column.sourceType] ?? (
              <SourceTypeUnknown size={20} />
            )}
            {column.name}
          </Box>
        )}
        value={selectedColumn ?? null}
        size="small"
        autoComplete
        blurOnSelect
        onBlur={props.onBlur}
        onChange={(e, value) => {
          e.stopPropagation();
          if (value) {
            props.onValueChange(value);
            props.onBlur?.();
          }
        }}
        renderInput={params => (
          <TextField {...params} variant="standard" placeholder="Column" />
        )}
      />
    </div>
  );
};

export const ColumnInlineEditor = (
  props: InlineEditorProps<Column> & { columns: Column[] }
) => {
  return (
    <InlineEditor
      {...props}
      widget={ColumnWidget}
      widgetProps={{ columns: props.columns }}
      prettifyFn={column => column?.name}
    />
  );
};
